// Motivos de "Não agendou" e "Não fechou" (VG) — puro.
//
// ★ "PERDIDO" DEIXOU DE SER ETAPA E VIROU MOTIVO. As duas colunas de "não" são
// VIVAS: a SDR continua ligando para reagendar, o negociador continua fazendo
// follow-up. O que diz se o lead ACABOU é o motivo — e cada motivo carrega
// `encerra`. É o que mantém a conversão de valor da VE de pé sem a etapa
// `perdido`: desfecho negativo = motivo que encerra.
//
// ★ `encerra` É GRAVADO NO LEAD NA HORA DE MOVER (`desfechoNegativoEm`), como o
// `tipo` do agendamento é gravado e nunca inferido depois. Mudar `encerra` na
// configuração vale para os PRÓXIMOS movimentos; o passado não é reescrito —
// senão um clique do gestor mudaria o mês fechado de agosto.
//
// ★ MOTIVO REMOVIDO = `ativo: false`. Some do select, continua legível onde já
// foi gravado. O id nunca é apagado nem reaproveitado.
//
// As listas padrão são as do dono. A configuração da clínica
// (`config.motivosNaoAgendou` / `config.motivosNaoFechou`) sobrepõe; a aba de
// Configurações semeia da MESMA fonte que o runtime lê (`config ?? PADRAO`).

export const ETAPAS_COM_MOTIVO = ['nao_agendou', 'nao_fechou'];

/** O campo do lead que guarda o motivo de cada etapa. */
export const CAMPO_MOTIVO = {
  nao_agendou: 'motivoNaoAgendou',
  nao_fechou: 'motivoNaoFechou',
};

/** O campo da config da clínica com a lista de cada etapa. */
export const CAMPO_CONFIG = {
  nao_agendou: 'motivosNaoAgendou',
  nao_fechou: 'motivosNaoFechou',
};

const m = (id, rotulo, encerra = false) => ({ id, rotulo, encerra, ativo: true });

export const MOTIVOS_NAO_AGENDOU_PADRAO = [
  m('nao_responde', 'Lead não responde'),
  m('vai_entrar_em_contato', 'Vai entrar em contato'),
  m('desistiu', 'Desistiu', true),
  m('fora_da_regiao', 'Fora da região', true),
  m('dentista', 'Dentista', true),
  m('candidato', 'Candidato', true),
  m('ja_e_paciente', 'Já é paciente', true),
  m('descarte', 'Descarte', true),
];

export const MOTIVOS_NAO_FECHOU_PADRAO = [
  m('vai_pensar', 'Vai pensar'),
  m('vai_falar_com_alguem', 'Vai falar com alguém'),
  m('achou_caro', 'Achou caro'),
  m('nao_tem_credito', 'Não tem crédito'),
  m('esta_cotando', 'Está cotando'),
  m('agendou_inicio', 'Agendou início'),
  m('vai_levantar_o_dinheiro', 'Vai levantar o dinheiro'),
  m('aguardando_limite_do_cartao', 'Aguardando limite do cartão'),
  m('nao_aguardou', 'Não aguardou a negociação', true),
];

const PADRAO = {
  nao_agendou: MOTIVOS_NAO_AGENDOU_PADRAO,
  nao_fechou: MOTIVOS_NAO_FECHOU_PADRAO,
};

const valido = (x) => x && typeof x.id === 'string' && x.id.length > 0;

/** Sanea uma lista vinda da config: forma fixa, booleanos estritos, sem ids repetidos. */
export function sanearMotivos(lista) {
  const vistos = new Set();
  const saida = [];
  for (const x of Array.isArray(lista) ? lista : []) {
    if (!valido(x) || vistos.has(x.id)) continue;
    vistos.add(x.id);
    saida.push({
      id: x.id,
      rotulo: String(x.rotulo ?? x.id).trim() || x.id,
      encerra: x.encerra === true,
      ativo: x.ativo !== false,
    });
  }
  return saida;
}

/**
 * A lista de uma etapa, pela config da clínica ou pelo padrão.
 *
 * ⚠ `config ?? PADRAO`, e não `config ?? []`: a mesma fonte para a tela de
 * mover e para a aba de Configurações — a contradição entre as duas foi o que
 * fechou a clínica na VF-3.
 */
export function listaDeMotivos(config, etapa) {
  const campo = CAMPO_CONFIG[etapa];
  if (!campo) return [];
  const daConfig = config?.[campo];
  return Array.isArray(daConfig) ? sanearMotivos(daConfig) : PADRAO[etapa] ?? [];
}

/** Só os que podem ser escolhidos agora. */
export const motivosAtivos = (config, etapa) => listaDeMotivos(config, etapa).filter((x) => x.ativo);

/** O motivo pelo id — inclusive inativo, para o passado continuar legível. */
export function motivoPorId(config, etapa, id) {
  if (!id) return null;
  return (
    listaDeMotivos(config, etapa).find((x) => x.id === id) ??
    // Id que não está em lista nenhuma (lista editada, clínica sem config
    // antiga): devolve algo legível em vez de sumir com o dado.
    { id, rotulo: id, encerra: false, ativo: false }
  );
}

/** Este motivo ENCERRA o lead (desfecho negativo)? */
export const motivoEncerra = (config, etapa, id) => motivoPorId(config, etapa, id)?.encerra === true;

/** id a partir do rótulo: "Vai falar com alguém" → "vai_falar_com_alguem"; sufixo se colidir. */
export function idDeMotivo(rotulo, existentes = []) {
  const base =
    String(rotulo ?? '')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'motivo';
  const usados = new Set((existentes ?? []).map((x) => x.id));
  if (!usados.has(base)) return base;
  let n = 2;
  while (usados.has(`${base}_${n}`)) n += 1;
  return `${base}_${n}`;
}

// ---- operações da aba de Configurações (puras: devolvem a lista nova) ----

export function adicionarMotivo(lista, rotulo, encerra = false) {
  const atual = sanearMotivos(lista);
  const limpo = String(rotulo ?? '').trim();
  if (!limpo) return atual;
  return [...atual, { id: idDeMotivo(limpo, atual), rotulo: limpo, encerra: encerra === true, ativo: true }];
}

/** "Remover" nunca apaga: marca inativo. Reativar é o inverso. */
export const inativarMotivo = (lista, id) =>
  sanearMotivos(lista).map((x) => (x.id === id ? { ...x, ativo: false } : x));

export const reativarMotivo = (lista, id) =>
  sanearMotivos(lista).map((x) => (x.id === id ? { ...x, ativo: true } : x));

export const alternarEncerra = (lista, id) =>
  sanearMotivos(lista).map((x) => (x.id === id ? { ...x, encerra: !x.encerra } : x));

export const renomearMotivo = (lista, id, rotulo) =>
  sanearMotivos(lista).map((x) =>
    x.id === id ? { ...x, rotulo: String(rotulo ?? '').trim() || x.rotulo } : x,
  );

/** Sobe (delta -1) ou desce (delta +1) — os mais comuns em cima. */
export function moverMotivo(lista, id, delta) {
  const atual = sanearMotivos(lista);
  const i = atual.findIndex((x) => x.id === id);
  const j = i + delta;
  if (i < 0 || j < 0 || j >= atual.length) return atual;
  const copia = [...atual];
  [copia[i], copia[j]] = [copia[j], copia[i]];
  return copia;
}
